import React, { useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import {
  DESKTOP_APP_LABEL,
  DESKTOP_APP_URL,
  PLATFORMS,
  buildPlayStoreUrl,
  getDeviceStore,
  getInstallEntryUrl,
} from '../config/appLinks';
import CopyButton from './CopyButton';
import InstallQr from './InstallQr';
import { track } from '../utils/analytics';

// Bloc d'installation de GetAppPage : les stores, le QR et les etats "pas
// encore publie", en un seul endroit.
//
// Il ne vit que sur cette page, jamais dans l'app en cours d'utilisation. Une
// invitation a installer plantee en permanence au-dessus du manche, c'est de la
// publicite qu'on regarde toute la session sans jamais y toucher ; celui qui
// arrive par le site l'a deja croisee la-bas.
//
// Ce qu'on montre depend de l'appareil qui regarde, pas d'un reglage :
//  - desktop : un QR, parce que le telephone est a cote et que c'est le geste
//    le plus court entre l'ecran et l'installation ;
//  - telephone ou tablette : le bouton du store correspondant, parce qu'on ne
//    scanne pas l'ecran qu'on tient.

const PLATFORM_ICONS = {
  ios: AppleIcon,
  android: AndroidIcon,
};

// Les badges officiels des deux stores, servis depuis public/assets/badges.
// Apple et Google fournissent chacun UNE image qu'on n'a pas le droit de
// redessiner ni de deformer : ni recolorisation, ni texte maison, ni etirement.
// D'ou la largeur calculee a partir du ratio d'origine plutot que laissee au
// navigateur, ce qui reserve aussi la place avant le chargement.
//
// `scale` corrige la seule chose qui les separe a l'oeil : le badge Google
// embarque sa marge de securite DANS le fichier, celui d'Apple non. A hauteur
// egale, le lettrage de Google parait 20% plus petit. Les 1.18 rendent les deux
// badges optiquement de la meme taille, comme le veulent les deux chartes.
//
// Le texte alternatif est le libelle exact du badge : c'est le nom accessible du
// lien, et un lecteur d'ecran doit entendre ce que les autres lisent.
const STORE_BADGES = {
  ios: {
    src: 'assets/badges/app-store.svg',
    alt: 'Download on the App Store',
    ratio: 119.66407 / 40,
    scale: 1,
  },
  android: {
    src: 'assets/badges/google-play.png',
    alt: 'Get it on Google Play',
    ratio: 646 / 250,
    scale: 1.18,
  },
};

const StoreBadge = ({ platformId, height }) => {
  const badge = STORE_BADGES[platformId];
  if (!badge) return null;
  const h = Math.round(height * badge.scale);
  return (
    <Box
      component="img"
      src={`${process.env.PUBLIC_URL}/${badge.src}`}
      alt={badge.alt}
      sx={{ display: 'block', height: `${h}px`, width: `${Math.round(h * badge.ratio)}px` }}
    />
  );
};

const PlatformRow = ({ platform, showStatus, onInstallClick }) => {
  const Icon = PLATFORM_ICONS[platform.id];
  const available = !!platform.url;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
        borderTop: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <Icon sx={{ fontSize: '1.25rem', color: 'rgba(255,255,255,0.5)' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: 'white', fontSize: '1.05rem', fontWeight: 500 }}>
          {platform.label}
        </Typography>
        {/* Inutile quand rien n'est publie : le bloc au-dessus vient de le dire,
            et le repeter par plateforme n'ajoute que du bruit. */}
        {!available && showStatus && (
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem' }}>
            Not out yet
          </Typography>
        )}
      </Box>

      {available ? (
        <Button
          href={platform.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            track('install_store_opened', { platform: platform.id, from: 'row' });
            onInstallClick?.({ platform: platform.id, method: 'store' });
          }}
          sx={{
            minWidth: 0,
            p: 0.5,
            borderRadius: '8px',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
          }}
        >
          <StoreBadge platformId={platform.id} height={32} />
        </Button>
      ) : (
        <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.55)', pr: 1 }}>
          Coming soon
        </Typography>
      )}
    </Box>
  );
};

// onInstallClick : la page qui accueille ce bloc mesure ce qu'elle veut du geste
// d'installation (le tunnel d'invitation, par exemple), sans que le bloc ait a
// savoir d'ou il est rendu.
// qrValue / qrCaption : la page d'invitation remplace la racine par le lien
// personnel du destinataire, pour que le telephone qui scanne arrive deja
// active. Sans eux, le QR pointe la racine comme partout ailleurs.
// primaryOnly : sur un telephone, seulement le bouton de SON store, sans la
// liste des autres plateformes. La page d'invitation l'utilise pour ne pas
// montrer "iPhone, not out yet" a quelqu'un qui tient un Android.
// qrCollapsible : le QR passe derriere un bouton (cf. InstallQr). Reserve a la
// page d'invitation, dont le QR porte un lien personnel.
// resolvePlayReferrer : la page d'invitation frappe ici, AU CLIC, le jeton qui
// voyagera dans le lien Google Play ; Play le rend a l'app au premier
// lancement, et l'Android qui installe depuis cette page s'ouvre deja connecte.
// Une fonction et pas une valeur : le jeton de passage vit une demi-heure, elle
// doit courir a partir du geste et pas de l'affichage de la page, et rien n'est
// frappe pour qui n'installe jamais. Absente partout ailleurs.
const GetTheApp = ({
  onInstallClick,
  qrValue,
  qrCaption,
  primaryOnly = false,
  qrCollapsible = false,
  resolvePlayReferrer = null,
}) => {
  const installUrl = useMemo(() => getInstallEntryUrl(), []);
  const detectedStore = useMemo(() => getDeviceStore(), []);
  const deviceStore = useMemo(
    () => (detectedStore ? PLATFORMS.find((p) => p.id === detectedStore.id) : null),
    [detectedStore]
  );
  const anyAvailable = PLATFORMS.some((p) => p.url);
  // Sur un appareil mobile, sa propre plateforme passe en bouton principal et
  // sort de la liste : elle y ferait doublon. Tant que rien n'est publie, il n'y
  // a pas de bouton principal et la liste reste complete.
  const rows =
    deviceStore && anyAvailable
      ? primaryOnly
        ? []
        : PLATFORMS.filter((p) => p.id !== deviceStore.id)
      : PLATFORMS;

  // Le bouton du store de l'appareil qui regarde, et lui seul : c'est le geste
  // du telephone qui installe, le seul que le referrer sert. Les lignes de
  // plateformes n'en portent pas, elles se cliquent depuis un ordinateur, ou le
  // chemin d'un appareil a l'autre passe par le QR.
  //
  // Le jeton se frappe au clic, puis la navigation part vers le store dans le
  // MEME onglet : un nouvel onglet ouvert apres une attente reseau se ferait
  // bloquer, et sur Android un lien Play suivi en direct ouvre l'app Play de
  // toute facon.
  //
  // Le href reste le store nu : clic milieu, "ouvrir dans un nouvel onglet" ou
  // JS en panne menent quand meme a l'installation, juste sans le raccourci de
  // connexion. Emission en echec : pareil, le store sans rien.
  const carriesReferrer = Boolean(resolvePlayReferrer) && deviceStore?.id === 'android';
  const onPrimaryClick = async (event) => {
    track('install_store_opened', { platform: deviceStore.id, from: 'primary' });
    onInstallClick?.({ platform: deviceStore.id, method: 'store' });
    if (!carriesReferrer) return;
    event.preventDefault();
    let token = '';
    try {
      token = (await resolvePlayReferrer()) || '';
    } catch {
      // Rien a transporter : le store nu, l'app se connectera par ses propres
      // moyens.
    }
    window.location.href = buildPlayStoreUrl(token);
  };

  return (
    <Box>
      {/* Avant la publication : ni QR ni bouton. Le telephone qui scannerait le
          code atterrirait sur ce meme bloc, sans rien a installer ; afficher un
          QR quand meme, c'est promettre une installation qui n'existe pas. */}
      {!anyAvailable ? (
        <Box
          sx={{
            py: 2,
            px: 2,
            borderRadius: '10px',
            textAlign: 'center',
            backgroundColor: 'rgba(255,255,255,0.06)',
          }}
        >
          <Typography sx={{ color: 'white', fontSize: '1.05rem', fontWeight: 500 }}>
            Not in the stores yet
          </Typography>
          <Typography
            sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', mt: 0.5, lineHeight: 1.5 }}
          >
            The mobile app is on its way. In the meantime, upScale runs in full in your computer's browser at {DESKTOP_APP_LABEL}.
          </Typography>
        </Box>
      ) : deviceStore ? (
        deviceStore.url ? (
          // Le badge officiel a la place du bouton orange maison : c'est la
          // seule presentation que les deux chartes autorisent, et c'est aussi
          // celle que les gens reconnaissent sans lire. Il garde sa taille
          // propre, centre dans une zone cliquable pleine largeur : un badge
          // etire sur la colonne serait hors charte.
          <Button
            fullWidth
            href={deviceStore.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onPrimaryClick}
            sx={{
              py: 1,
              borderRadius: '10px',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            }}
          >
            <StoreBadge platformId={deviceStore.id} height={50} />
          </Button>
        ) : (
          // Sa plateforme n'est pas publiee, mais une autre l'est : le bloc
          // "rien n'est sorti" ne s'affiche pas et cet appareil se retrouvait
          // sans aucune action. Le navigateur d'ordinateur en est une vraie,
          // c'est l'app entiere et elle est disponible aujourd'hui.
          //
          // L'iPhone est le cas du jour (APP_STORE_URL vide) : il le dit en
          // clair et n'affiche aucun bouton App Store fantome. La sortie est
          // l'adresse a copier, pas un lien a suivre : un telephone qui ouvrirait
          // upscale.li/app retomberait sur cette meme page (isBrowserGated).
          <Box
            sx={{
              py: 2,
              px: 2,
              borderRadius: '10px',
              backgroundColor: 'rgba(255,255,255,0.06)',
            }}
          >
            <Typography sx={{ color: 'white', fontSize: '1.05rem', fontWeight: 500 }}>
              {deviceStore.id === 'ios' ? 'iPhone: not yet.' : `Not on the ${deviceStore.store} yet`}
            </Typography>
            <Typography
              sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', mt: 0.5, lineHeight: 1.5 }}
            >
              {deviceStore.id === 'ios'
                ? 'Play on your computer now.'
                : "Play right now in your computer's browser. It's the full app, not a preview: every scale, every backing track, nothing to install."}
            </Typography>
            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.75,
                pl: 1.5,
                borderRadius: '8px',
                backgroundColor: 'rgba(0,0,0,0.35)',
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 500,
                  overflowWrap: 'anywhere',
                }}
              >
                {DESKTOP_APP_LABEL}
              </Typography>
              <CopyButton
                value={DESKTOP_APP_URL}
                label="Copy link"
                onCopy={() => {
                  track('install_desktop_link_copied', { platform: deviceStore.id });
                  onInstallClick?.({ platform: 'desktop', method: 'copy_link' });
                }}
              />
            </Box>
          </Box>
        )
      ) : (
        <InstallQr
          value={qrValue || installUrl}
          title="Open the upScale install page on your phone"
          caption={qrCaption}
          collapsible={qrCollapsible}
        />
      )}

      {rows.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          {rows.map((platform) => (
            <PlatformRow
              key={platform.id}
              platform={platform}
              showStatus={anyAvailable}
              onInstallClick={onInstallClick}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GetTheApp;
